import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Launchpool } from '@flipster/apis/v1/launchpool/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useLaunchpool } from '@flipster/features/src/shared/launchpool/state';
import { FormattedMessage } from '@flipster/i18n';
import { Button, Toggle } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { snackbar } from '@flipster/react/design-system/snackbar';
import Formatted from '@flipster/react/design-utils/formatted';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { StepModel } from '@flipster/react/misc/step/use-steps';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/utils/alert';
import { getFlipsterConfigsV1Key, getLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { Steps } from '..';
import { StakingFormSchema } from '../../validation';
import StakingCurrencyTradabilityInfo from '../staking-currency-tradability-info';

interface Props {
  pool: Launchpool;
  onClose: () => void;
  setModalStep: (step: StepModel<Steps>) => void;
}

const StakingCompletedForm = ({ pool, onClose, setModalStep }: Props) => {
  const { id, stakingCurrency, status, autoConversionOn, autoConversionSupported, autoConversionTooltip, showHedge } =
    pool;
  const { user } = useAuth();
  const { postLaunchpoolAutoConversionMutation } = useLaunchpool();
  const { isMobile } = useBreakpoint();

  const [isTurnOn, setIsTurnOn] = useState(!!autoConversionOn);

  const { getValues } = useFormContext<StakingFormSchema>();

  const stakingAmount = getValues('amount');
  const isShowHedgeButton = showHedge && !isMobile;

  const handleChangeToggle = async () => {
    setIsTurnOn((prev) => !prev);
    try {
      await postLaunchpoolAutoConversionMutation.mutateAsync({
        launchpoolId: id,
        data: { turnOn: !isTurnOn },
      });
      segment.track(StakeEvent.StakeAutoConvertClick, {
        currency: stakingCurrency,
        status,
        set_on: !isTurnOn,
        event_location: 'staking_complete_page',
      });
      snackbar.success({
        message: isTurnOn ? 'Earn setup has been cancelled' : 'Success! Your assets will be invested in Flipster Earn',
      });
    } catch (error) {
      alert.error(error);
    }
  };

  const checkSkipHedgingNotice = () => {
    if (!user?.userId) return;
    const skipHedgingNoticeKey = getFlipsterConfigsV1Key(user.userId, 'skipHedgingNoticeConfirm');
    return getLocalStorage(skipHedgingNoticeKey);
  };

  const onClickDone = () => {
    if (!checkSkipHedgingNotice() && isShowHedgeButton) {
      setModalStep({ step: 'hedgingNotice', context: undefined });
      return;
    }
    onClose();
  };

  const onClickHedging = () => {
    segment.track(StakeEvent.StakeHedgeClick, { currency: stakingCurrency, event_location: 'staking_complete_page' });
    if (!checkSkipHedgingNotice()) {
      setModalStep({ step: 'hedgingNotice', context: undefined });
      return;
    }
    setModalStep({ step: 'hedging', context: undefined });
  };

  return (
    <form>
      <Modal.Content>
        <header className={css({ textAlign: 'center', pb: 16 })}>
          <CheckCircleIcon size={40} className={css({ color: 'content.accent.info', mb: 24 })} />
          <p className={css({ textStyle: 'Title/S', color: 'content.accent.info', mb: 12 })}>
            <FormattedMessage id='Assets.r250925.Staking_completed' />
          </p>
          <Formatted.Number
            value={stakingAmount}
            suffix={stakingCurrency}
            css={{ textStyle: 'Title/L', color: 'content.default.level-4' }}
          />
        </header>
        <StakingCurrencyTradabilityInfo stakingCurrency={stakingCurrency} />
        {autoConversionSupported && (
          <div className={css({ py: 12, px: 16, bgColor: 'background.default.level-2', rounded: 8, mt: 24 })}>
            <div
              className={hstack({
                gap: 16,
                mb: 8,
              })}
            >
              <div className={css({ flex: 1, textStyle: 'Text/M/Strong', color: 'content.default.level-4' })}>
                <p>{autoConversionTooltip.title} </p>
                <p className={css({ color: 'content.state.positive' })}>{autoConversionTooltip.highlightedTitle}</p>
              </div>
              <Toggle selected={isTurnOn} onChange={handleChangeToggle} />
            </div>
            <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
              After the launch pool ends, the staked asset will be automatically converted to USDT.
            </p>
          </div>
        )}
      </Modal.Content>
      <Modal.Footer css={isShowHedgeButton && { flexDir: 'column', alignItems: 'center', '& > button': { w: 328 } }}>
        <Button as='button' hierarchy='primary' size='large' onClick={onClickDone} label='Done' />
        {isShowHedgeButton && (
          <Button
            as='button'
            size='large'
            color='neutral'
            hierarchy='secondary'
            onClick={onClickHedging}
            label='Go hedge to protect staked assets'
          />
        )}
      </Modal.Footer>
    </form>
  );
};

export default StakingCompletedForm;
